import { Controller } from "@hotwired/stimulus"

/**
 * Expense Type Controller
 *
 * Controls visibility of fields based on selected expense_type:
 * - vpd, simplified_receipt: datetime-local picker, hide delivery/due dates, hide symbols/bank account
 * - received_invoice, advance_invoice, advance_tax_document, insurance_settlement: date picker, show
 *   delivery/due dates, show symbols/bank account (invoiceTypes value) — all carry a document date and
 *   a reference number (for a krycí list, the insurer's claim number)
 * - internal_document: date picker, hide all optional fields
 * - blank: datetime-local picker, show all fields
 *
 * Also swaps the advance/DPP text-detection siblings: the advanceWarning target shows while the
 * select holds another type than the detected one (data-detected-type), the advanceNote target
 * once it holds the detected type. The view renders neither without a detected type.
 */
export default class extends Controller {
  static targets = [
    "issueDatetime",
    "issueDate",
    "issueTime",
    "deliveryDate",
    "dueDate",
    "invoiceFields",
    "receiptFields",
    "advanceWarning",
    "advanceNote"
  ]

  static values = {
    receiptTypes: { type: Array, default: ["vpd", "simplified_receipt"] },
    invoiceTypes: {
      type: Array,
      default: ["received_invoice", "advance_invoice", "advance_tax_document", "insurance_settlement"]
    }
  }

  connect() {
    this.toggle()
  }

  toggle() {
    const type = this.element.querySelector("select[name*='expense_type']")?.value || ""
    const isReceipt = this.receiptTypesValue.includes(type)
    const showTime = isReceipt || type === ""

    if (showTime) {
      this.#switchToDatetime()
    } else {
      this.#switchToDate()
    }

    if (isReceipt || type === "internal_document") {
      this.#hide(this.deliveryDateTargets)
      this.#hide(this.dueDateTargets)
    } else {
      this.#show(this.deliveryDateTargets)
      this.#show(this.dueDateTargets)
    }

    // Symbols and bank account only for invoice-shaped types (or blank = show all)
    if (this.invoiceTypesValue.includes(type) || type === "") {
      this.#show(this.invoiceFieldsTargets)
    } else {
      this.#hide(this.invoiceFieldsTargets)
    }

    // Receipt fields (receipt_id, cash_register_code, okp) only for receipt types (or blank = show all)
    if (isReceipt || type === "") {
      this.#show(this.receiptFieldsTargets)
    } else {
      this.#hide(this.receiptFieldsTargets)
    }

    this.#toggleAdvanceBanners(type)
  }

  syncHiddenFields() {
    const input = this.issueDatetimeTarget
    const value = input.value

    if (input.type === "datetime-local" && value) {
      const [date, time] = value.split("T")
      this.issueDateTarget.value = date || ""
      this.issueTimeTarget.value = time || ""
    } else if (input.type === "date") {
      this.issueDateTarget.value = value || ""
      this.issueTimeTarget.value = ""
    }
  }

  #switchToDatetime() {
    const input = this.issueDatetimeTarget
    if (input.type === "datetime-local") return

    const date = this.issueDateTarget.value
    const time = this.issueTimeTarget.value
    input.type = "datetime-local"
    if (date) {
      input.value = time ? `${date}T${time}` : `${date}T00:00`
    }
  }

  #switchToDate() {
    const input = this.issueDatetimeTarget
    if (input.type === "date") return

    const currentValue = input.value
    input.type = "date"
    if (currentValue) {
      const [date] = currentValue.split("T")
      input.value = date
      this.issueDateTarget.value = date
      this.issueTimeTarget.value = ""
    }
  }

  #toggleAdvanceBanners(type) {
    const matches = (el) => el.dataset.detectedType === type
    this.advanceWarningTargets.forEach((el) => el.classList.toggle("d-none", matches(el)))
    this.advanceNoteTargets.forEach((el) => el.classList.toggle("d-none", !matches(el)))
  }

  #show(targets) {
    targets.forEach((el) => el.classList.remove("d-none"))
  }

  #hide(targets) {
    targets.forEach((el) => el.classList.add("d-none"))
  }
}
